import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderSlot as _renderSlot, vShow as _vShow, withDirectives as _withDirectives, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeProps as _normalizeProps, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = ["aria-modal", "aria-label", "aria-labelledby", "aria-describedby", "aria-hidden"]
const _hoisted_3 = { class: "bs-dialog__scroll-layout" }
const _hoisted_4 = { class: "bs-dialog__scroll-layout__header" }
const _hoisted_5 = { class: "bs-dialog__scroll-layout__body" }
const _hoisted_6 = { class: "bs-dialog__scroll-layout__footer" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", {
  class: "bs-dialog__focus-trap-tab-placeholder",
  tabindex: "0",
  role: "none"
}, null, -1)

import { useStackedDialogCounter } from './BsDialog.utils'
import { FocusTrap } from 'focus-trap-vue'
import { createAutoIncrementIdProp } from '@/utils/props'


import {
  defineComponent,
  defineProps,
  defineEmits,
  normalizeClass,
  ref,
  computed,
  getCurrentInstance,
  unref,
  onMounted,
  onUnmounted,
  watch
} from 'vue'
const __default__ = defineComponent({
  name: 'BsDialog'
})

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  id: createAutoIncrementIdProp('bs-dialog'),
  classNames: {
    type: [Object, Array, String],
    default: () => undefined
  },
  ariaLabel: {
    type: String,
    default: () => undefined
  },
  ariaLabelledBy: {
    type: String,
    default: () => undefined
  },
  ariaDescribedBy: {
    type: String,
    default: () => undefined
  },
  visible: {
    type: Boolean,
    default: false
  },
  closeOnEscape: {
    type: Boolean,
    default: false
  },
  closeOnBackdropClick: {
    type: Boolean,
    default: false
  },
  hideFooter: {
    type: Boolean,
    default: false
  },
  mobileFloating: {
    type: Boolean,
    default: false
  }
},
  emits: ['opened', 'closed', 'update:visible'],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

__expose({ open, close })

const instance = getCurrentInstance()
const opened = ref(false)
const stackedDialogCount = ref(0)
const stackedDialogCountPosition = ref(0)
const backdrop = ref<HTMLDivElement>()
const stackedDialogCounter = useStackedDialogCounter()

const isFirstDialogOnStack = computed(() => {
  return stackedDialogCountPosition.value === 1
})
const isLastDialogOnStack = computed(() => {
  return stackedDialogCountPosition.value === stackedDialogCount.value
})
const ariaModal = computed(() => {
  return opened.value && isFirstDialogOnStack.value
})
const incomingClassNames = computed((): string => {
  return normalizeClass(props.classNames) ?? ''
})
const parentScopedId = computed((): string | null => {
  if (incomingClassNames.value) {
    return instance?.parent?.proxy?.$options?.__scopeId ?? null
  }
  return null
})

watch(stackedDialogCounter.count, (count) => {
  stackedDialogCount.value = count
})

watch(() => props.visible, (visible) => {
  if (visible) {
    open()
  } else {
    close()
  }
})

onMounted(() => {
  if (props.visible) {
    open()
  }
})

onUnmounted(() => {
  removeWindowEscapeClose()
  if (opened.value) {
    stackedDialogCounter.decrement()
  }
})

function open() {
  if (opened.value) {
    return
  }
  opened.value = true
  addWindowEscapeClose()
  stackedDialogCounter.increment()
  stackedDialogCountPosition.value = unref(stackedDialogCounter.count)
  emit('opened', instance)
  emit('update:visible', true)
}

function close() {
  if (!opened.value) {
    return
  }
  opened.value = false
  removeWindowEscapeClose()
  stackedDialogCounter.decrement()
  stackedDialogCountPosition.value = 0
  emit('closed', instance)
  emit('update:visible', false)
}

function addWindowEscapeClose() {
  window.addEventListener('keydown', handleWindowEscapeKeyDown)
}

function removeWindowEscapeClose() {
  window.removeEventListener('keydown', handleWindowEscapeKeyDown)
}

function handleWindowEscapeKeyDown(event: KeyboardEvent) {
  if (props.closeOnEscape && opened.value && event.key === 'Escape') {
    close()
  }
}

function handleBackdropClick(event: MouseEvent) {
  if (props.closeOnBackdropClick && event.currentTarget === backdrop.value) {
    close()
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "bs-dialog--fade" }, {
    default: _withCtx(() => [
      (opened.value)
        ? (_openBlock(), _createElementBlock("div", _normalizeProps({
            key: 0,
            id: __props.id,
            class: ["bs-dialog", incomingClassNames.value],
            [parentScopedId.value || ""]: ''
          }), [
            _createElementVNode("div", {
              ref_key: "backdrop",
              ref: backdrop,
              class: _normalizeClass(["bs-dialog__backdrop", {
          'bs-dialog__backdrop--hidden': !isLastDialogOnStack.value
        }]),
              onClick: handleBackdropClick
            }, null, 2),
            _createElementVNode("div", {
              role: "dialog",
              class: _normalizeClass(["bs-dialog__dialog", {
          'bs-dialog__dialog--behind': !isLastDialogOnStack.value,
          'bs-dialog__dialog--mobile-fullscreen': !__props.mobileFloating
        }]),
              "aria-modal": ariaModal.value,
              "aria-label": __props.ariaLabel,
              "aria-labelledby": __props.ariaLabelledBy,
              "aria-describedby": __props.ariaDescribedBy,
              "aria-hidden": !opened.value
            }, [
              _createVNode(_unref(FocusTrap), null, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, [
                      _renderSlot(_ctx.$slots, "header", { close: close })
                    ]),
                    _createElementVNode("div", _hoisted_5, [
                      _renderSlot(_ctx.$slots, "default", { close: close })
                    ]),
                    _withDirectives(_createElementVNode("div", _hoisted_6, [
                      _renderSlot(_ctx.$slots, "footer", { close: close })
                    ], 512), [
                      [_vShow, !__props.hideFooter]
                    ]),
                    _hoisted_7
                  ])
                ]),
                _: 3
              })
            ], 10, _hoisted_2)
          ], 16, _hoisted_1))
        : _createCommentVNode("", true)
    ]),
    _: 3
  }))
}
}

})